/**
 * Utility for report filtering logic.
 * Ported and adapted from Sunny Homes version2.o app.js
 */

export const DATE_RANGES = {
    ALL: 'all',
    THIS_MONTH: 'thisMonth',
    LAST_MONTH: 'lastMonth',
    LAST_3_MONTHS: 'last3',
    THIS_YEAR: 'thisYear',
    CUSTOM: 'custom',
};

/**
 * Converts the quick range picker value to { fromDate, toDate } ISO strings for the API.
 */
export function buildDatePayload(filters) {
    const now = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];

    if (filters.quickRange === DATE_RANGES.THIS_MONTH) {
        return { fromDate: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), toDate: null };
    }
    if (filters.quickRange === DATE_RANGES.LAST_MONTH) {
        return {
            fromDate: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
            toDate: fmt(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
    }
    if (filters.quickRange === DATE_RANGES.LAST_3_MONTHS) {
        return { fromDate: fmt(new Date(now.getFullYear(), now.getMonth() - 3, 1)), toDate: null };
    }
    if (filters.quickRange === DATE_RANGES.THIS_YEAR) {
        return { fromDate: fmt(new Date(now.getFullYear(), 0, 1)), toDate: null };
    }
    if (filters.quickRange === DATE_RANGES.CUSTOM) {
        return { fromDate: filters.fromDate || null, toDate: filters.toDate || null };
    }
    return { fromDate: null, toDate: null };
}

/**
 * Maps status for display (Matches app.js logic)
 */
export function displayStatus(s) {
    if (!s) return 'N/A';
    if (s === 'Moved to BuilderTrend') return 'Converted';
    if (s === 'Lost  Not Proceeding Ahead' || s === 'Lost Not Proceeding Ahead' || s === 'Lost') return 'Dead/Lost';
    if (s === 'Sold  Contract Signed') return 'Sold';
    if (s === 'Meeting Held' || s === 'Meeting Held ') return 'Meeting Held';
    if (s === 'Follow up required' || s === 'Follow up scheduled') return 'Follow Up';
    if (s === 'Disqualified' || s === 'Disqualified ') return 'Disqualified';
    if (s === 'Quote Sent' || s === 'Quote Sent ') return 'Quote Sent';
    return s;
}

/**
 * Helper to check if a lead is converted
 */
export function isConverted(lead) {
    const s = lead.status;
    return s === 'Moved to BuilderTrend' || s === 'Sold  Contract Signed' || s === 'Sold';
}

/**
 * Helper to check if a lead is dead
 */
export function isDead(lead) {
    const s = lead.status;
    return s === 'Dead Lead' || s === 'Lost Not Proceeding Ahead' || s === 'Lost  Not Proceeding Ahead' || s === 'Disqualified' || s === 'Disqualified ' || s === 'Dead' || s === 'Lost';
}

/**
 * Main filtering logic for leads
 */
export function getFilteredLeads(leads, filters) {
    if (!leads) return [];
    
    const { quickRange, fromDate, toDate, agent, status, inquiry, search } = filters;
    
    let filtered = leads.filter(lead => {
        // Exclude spam and Mcnamara (as per app.js)
        if (lead.agent === 'Spam' || lead.status === 'Spam' || lead.agent === 'Mcnamara') return false;
        return true;
    });

    // Date Filtering
    let from = null, to = null;
    const now = new Date();
    
    if (quickRange === DATE_RANGES.THIS_MONTH) {
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = now;
    } else if (quickRange === DATE_RANGES.LAST_MONTH) {
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (quickRange === DATE_RANGES.LAST_3_MONTHS) {
        from = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        to = now;
    } else if (quickRange === DATE_RANGES.THIS_YEAR) {
        from = new Date(now.getFullYear(), 0, 1);
        to = now;
    } else if (quickRange === DATE_RANGES.CUSTOM) {
        if (fromDate) from = new Date(fromDate);
        if (toDate) {
            to = new Date(toDate);
            to.setHours(23, 59, 59);
        }
    }

    if (from || to) {
        filtered = filtered.filter(d => {
            const dt = new Date(d.date);
            if (from && dt < from) return false;
            if (to && dt > to) return false;
            return true;
        });
    }

    // Agent Filtering
    if (agent) {
        filtered = filtered.filter(d => d.agent === agent);
    }

    // Status Filtering
    if (status) {
        filtered = filtered.filter(d => displayStatus(d.status) === status || d.status === status);
    }

    // Inquiry Filtering
    if (inquiry) {
        filtered = filtered.filter(d => d.inquiry === inquiry);
    }

    // Search Filtering (for All Leads)
    if (search) {
        const s = search.toLowerCase();
        filtered = filtered.filter(d => 
            (d.name || '').toLowerCase().includes(s) || 
            (d.email || '').toLowerCase().includes(s) || 
            (d.comment || '').toLowerCase().includes(s)
        );
    }

    return filtered;
}

/**
 * Get unique agents from leads
 */
export function getUniqueAgents(leads) {
    if (!leads) return [];
    const agents = leads
        .map(l => l.agent)
        .filter(a => a && a !== 'Spam' && a !== 'Mcnamara');
    return [...new Set(agents)].sort();
}

/**
 * Get unique statuses from leads (displayed names)
 */
export function getUniqueStatuses(leads) {
    if (!leads) return [];
    const statuses = leads
        .filter(l => l.status && l.status !== 'Spam')
        .map(l => displayStatus(l.status));
    return [...new Set(statuses)].sort();
}

/**
 * Get unique inquiry types from leads
 */
export function getUniqueInquiries(leads) {
    if (!leads) return [];
    const inqs = leads
        .map(l => l.inquiry)
        .filter(Boolean);
    return [...new Set(inqs)].sort();
}
